import capitalize from 'lodash/capitalize';

export const TEMPLATE_GROUPS = Object.freeze({
    GUEST: 'GUEST',
    HOST: 'HOST',
    OWNER: 'OWNER',
    OTHER: 'OTHER',
    CUSTOM: 'CUSTOM',
});

export const TEMPLATE_TYPES = Object.freeze({
    // Guest
    PRE_BOOKING: {
        value: 'PRE_BOOKING',
        text: 'Pre Booking',
    },
    POST_BOOKING: {
        value: 'POST_BOOKING',
        text: 'Post Booking',
    },
    TRAVEL_INSURANCE: {
        value: 'TRAVEL_INSURANCE',
        text: 'Travel Insurance',
    },
    PAYMENT_REQUESTS: {
        value: 'PAYMENT_REQUESTS',
        text: 'Payment Requests',
    },
    GUEST_VERIFY: {
        value: 'GUEST_VERIFY',
        text: 'Guest Verify',
    },
    // Host
    NOTIFICATIONS: {
        value: 'NOTIFICATIONS',
        text: 'Notifications',
    },
    // Other
    FUTURE_STAY_CREDIT: {
        value: 'CUSTOM',
        text: 'Custom',
    },
    STAYLIO_APP: {
        value: 'STAYLIO_APP',
        text: 'Staylio App',
    },
    USER: {
        value: 'USER',
        text: 'User',
    },
    RENTAL_AGREEMENT: {
        value: 'RENTAL_AGREEMENT',
        text: 'Rental Agreement',
    },
});

export const GROUPPED_TEMPLATE_TYPES = {
    [TEMPLATE_GROUPS.GUEST]: [
        TEMPLATE_TYPES.PRE_BOOKING,
        TEMPLATE_TYPES.POST_BOOKING,
        TEMPLATE_TYPES.TRAVEL_INSURANCE,
        TEMPLATE_TYPES.PAYMENT_REQUESTS,
        TEMPLATE_TYPES.GUEST_VERIFY,
        TEMPLATE_TYPES.RENTAL_AGREEMENT,
    ],
    [TEMPLATE_GROUPS.HOST]: [TEMPLATE_TYPES.NOTIFICATIONS],
    [TEMPLATE_GROUPS.OWNER]: [TEMPLATE_TYPES.NOTIFICATIONS],
    [TEMPLATE_GROUPS.OTHER]: [TEMPLATE_TYPES.FUTURE_STAY_CREDIT, TEMPLATE_TYPES.STAYLIO_APP],
    [TEMPLATE_GROUPS.CUSTOM]: [TEMPLATE_TYPES.USER],
};

export const TEMPLATE_IDENTIFIERS = Object.freeze({
    // Guest - Pre-Booking
    GUEST_QUOTE: 'guest_quote',
    PROPERTY_NOT_AVAILABLE: 'property_not_available',

    // Guest - Post-Booking
    GUEST_FOLIO: 'guest_folio',
    BOOKING_CONFIRMATION: 'booking_confirmation',
    REMAINING_BALANCE_DUE: 'remaining_balance_due',
    RENTAL_AGREEMENT_REQUEST: 'rental_agreement_request',
    RENTAL_AGREEMENT_DOWNLOAD: 'rental_agreement_download',
    PRE_ARRIVAL: 'pre_arrival',
    ARRIVAL: 'arrival',
    REVIEW_REQUEST: 'review_request',

    // Guest - Travel Insurance
    PURCHASE_REMINDER: 'purchase_reminder',

    // Guest - Payment Requests
    PAYMENT_REQUEST: 'payment_request',
    PAYMENT_FAILURE: 'payment_request_failure',

    // Guest - Guest Verify
    REQUEST_ID: 'request_id',
    SUCCESS_ID: 'success_id',

    // Host - Notifications
    NEW_GUEST_BOOKING: 'new_guest_booking',
    RENTAL_AGREEMENT_VIEWED: 'rental_agreement_viewed',
    RENTAL_AGREEMENT_SIGNED: 'rental_agreement_signed',
    GUEST_VERIFY_SUCCESS: 'guest_verify_success',
    NEW_OWNER_BLOCK: 'new_owner_block',
    OWNER_BLOCK_DELETE: 'owner_block_delete',
    WORK_ORDER_STATUS_CHANGE: 'work_order_status_change',

    // Owner - Notifications
    LOGIN_DETAILS: 'login_details',

    // Other - Future Stay Credit
    GUEST_CREDIT_NOTIFICATION: 'guest_credit_notification',

    // Other - Staylio App
    INTRO_GUEST: 'intro_guest',
});

export const getTemplateTypeLabel = (templateType) => {
    return templateType
        .split('_')
        .map((word) => capitalize(word))
        .join(' ');
};

export const MOST_COMMON_TAGS = [
    '[GUESTFIRSTNAME]',
    '[GUESTLASTNAME]',
    '[GUESTEMAIL]',
    '[GUESTPHONE]',
    '[PNAME]',
    '[PADDRESS]',
    '[RARRIVEDATE]',
    '[RDEPARTDATE]',
    '[RCHECKINTTIME]',
    '[RCHECKOUTTIME]',
    '[RTOTAL]',
    '[RBALANCE]',
    '[RINVOICENUM]',
    '[RINVOICEDATE]',
    '[RDEPOSIT]',
    '[RGUESTS_TOTAL]',
    '[DOOR_CODE]',
    '[P_WIFI_NETWORK_NAME]',
    '[P_WIFI_NETWORK_PASSWORD]',
    '[BOOKING_SUMMARY]',
    '[GUEST_FOLIO]',
];

export const FONT_FAMILY_OPTIONS = [
    {
        label: 'Andale Mono',
        value: 'andale mono,times',
    },
    {
        label: 'Arial',
        value: 'arial,helvetica,sans-serif',
    },
    {
        label: 'Arial Black',
        value: 'arial black,avant garde,arial',
    },
    {
        label: 'Book Antiqua',
        value: 'book antiqua,palatino',
    },
    {
        label: 'Comic Sans MS',
        value: 'comic sans ms,sans-serif',
    },
    {
        label: 'Courier New',
        value: 'courier new,courier',
    },
    {
        label: 'Georgia',
        value: 'georgia,palatino',
    },
    {
        label: 'Helvetica',
        value: 'helvetica,sans-serif',
    },
    {
        label: 'Impact',
        value: 'impact,chicago',
    },
    {
        label: 'Symbol',
        value: 'symbol',
    },
    {
        label: 'Tahoma',
        value: 'tahoma,arial,helvetica,sans-serif',
    },
    {
        label: 'Terminal',
        value: 'terminal,monaco',
    },
    {
        label: 'Times New Roman',
        value: 'times new roman,times',
    },
    {
        label: 'Trebuchet MS',
        value: 'trebuchet ms,geneva',
    },
    {
        label: 'Verdana',
        value: 'verdana,geneva',
    },
    {
        label: 'Lobster Two',
        value: "'Lobster Two',cursive",
        url: 'https://fonts.googleapis.com/css?family=Lobster+Two:400,700',
    },
    {
        label: 'Playfair Display',
        value: "'Playfair Display',serif",
        url: 'https://fonts.googleapis.com/css?family=Playfair+Display:400,700',
    },
    {
        label: 'Rubik',
        value: "'Rubik',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Rubik:400,700',
    },
    {
        label: 'Source Sans Pro',
        value: "'Source Sans Pro',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Source+Sans+Pro:400,700',
    },
    {
        label: 'Open Sans',
        value: "'Open Sans',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Open+Sans:400,700',
    },
    {
        label: 'Crimson Text',
        value: "'Crimson Text',serif",
        url: 'https://fonts.googleapis.com/css?family=Crimson+Text:400,700',
    },
    {
        label: 'Montserrat',
        value: "'Montserrat',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Montserrat:400,700',
    },
    {
        label: 'Old Standard TT',
        value: "'Old Standard TT',serif",
        url: 'https://fonts.googleapis.com/css?family=Old+Standard+TT:400,700',
    },
    {
        label: 'Lato',
        value: "'Lato',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Lato:400,700',
    },
    {
        label: 'Raleway',
        value: "'Raleway',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Raleway:400,700',
    },
    {
        label: 'Cabin',
        value: "'Cabin',sans-serif",
        url: 'https://fonts.googleapis.com/css?family=Cabin:400,700',
    },
    {
        label: 'Pacifico',
        value: "'Pacifico',cursive",
        url: 'https://fonts.googleapis.com/css?family=Pacifico',
    },
];
